/**
 * Money display and parsing for the billing surfaces.
 *
 * The contract's `Money` value type is the canonical shape: an integer amount
 * in the currency's smallest unit plus an ISO-4217 code. `formatMoney` renders
 * it honouring the currency's own minor-unit scale — never assuming 100 cents
 * to a unit. The bare-cents helpers (`formatUsd`, `parseDollarsToCents`,
 * `centsToDollarInput`) stay for the USD-denominated overage editor and the
 * clearly-labeled preview fixtures; live contract figures go through
 * `formatMoney`.
 */
import type { components } from "@scalty/contracts";

type Money = components["schemas"]["Money"];
type Balance = components["schemas"]["Balance"];

/**
 * Formats a contract `Money` value, e.g. {amountCents:247, currency:"usd"} →
 * "$2.47". The number of minor-unit digits comes from the currency itself
 * (2 for USD, 0 for JPY, 3 for BHD), so the amount is scaled correctly for any
 * currency — no hardcoded /100. Falls back to a bare `amount code` string for
 * an unrecognized currency code rather than throwing.
 */
export function formatMoney(money: Money): string {
  const currency = money.currency.toUpperCase();
  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat("en-US", { style: "currency", currency });
  } catch {
    return `${(money.amountCents / 100).toFixed(2)} ${currency}`;
  }
  const minorDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return formatter.format(money.amountCents / 10 ** minorDigits);
}

/** True when a `Money` amount is strictly greater than zero. */
export function isPositiveMoney(money: Money): boolean {
  return money.amountCents > 0;
}

/**
 * `a - b` in the same currency, clamped at zero — for the net-owed-after-credit
 * figure. Throws on a currency mismatch rather than silently producing a wrong
 * amount (the two operands always come from the same Balance, so a mismatch is
 * a contract bug, not a runtime condition).
 */
export function subtractMoneyClamped(a: Money, b: Money): Money {
  if (a.currency !== b.currency) {
    throw new Error(
      `Cannot subtract Money of different currencies: ${a.currency} and ${b.currency}`,
    );
  }
  return { amountCents: Math.max(0, a.amountCents - b.amountCents), currency: a.currency };
}

/** Formats integer cents as a dollar amount, e.g. 247 → "$2.47". */
export function formatUsd(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}$${dollars.toLocaleString("en-US")}.${String(remainder).padStart(2, "0")}`;
}

/** Formats a "YYYY-MM" usage period as a human month, e.g. "June 2026". */
export function formatUsagePeriod(period: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return period;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Parses a user-typed dollar amount into integer cents.
 * Returns null for anything that is not a plain non-negative amount with at
 * most two decimal places (no signs, exponents, or currency symbols).
 */
export function parseDollarsToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole = "0", fraction = ""] = trimmed.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0") || "0");
}

/** Renders integer cents as a plain dollar input value, e.g. 2000 → "20.00". */
export function centsToDollarInput(cents: number): string {
  const dollars = Math.floor(cents / 100);
  const remainder = Math.abs(cents % 100);
  return `${dollars}.${String(remainder).padStart(2, "0")}`;
}

/**
 * The limit the organisation is actually working against, and whether the
 * recorded overage allowance is part of it.
 *
 * The figure is the server's: `remaining` is measured against it, and only the
 * server sees every condition that can withdraw the allowance — a missing
 * payment method, an unpaid invoice, an operator suspension. Recomputing it
 * here would put the same rule on two sides and let them disagree on the same
 * screen, so this reads `effectiveLimit` and only compares it with the credit
 * to know whether the allowance counted.
 */
export function spendingCeiling(balance: Balance): { limit: Money; overageInEffect: boolean } {
  return {
    limit: balance.effectiveLimit,
    overageInEffect: balance.effectiveLimit.amountCents > balance.credit.amountCents,
  };
}

/**
 * One sentence saying what the recorded overage allowance is doing right now.
 * Whether it counts is read off the effective limit rather than re-derived;
 * the flags only choose the wording, and a condition the contract does not
 * expose falls through to a statement that claims no particular cause.
 */
export function overageStanding(balance: Balance): string {
  if (spendingCeiling(balance).overageInEffect) return "The overage allowance raises the limit above the monthly credit.";
  if (balance.overageLimit.amountCents === 0) return "No overage is allowed: work stops when the monthly credit is spent.";
  if (!balance.paymentMethodPresent) return "The overage allowance is recorded; it raises the limit once a payment method is on file.";
  if (balance.delinquent) return "The overage allowance is recorded but suspended while the account is overdue.";
  return "The overage allowance is recorded but is not in effect on this account.";
}

/**
 * Whether a projected operation takes the period past the limit in force.
 *
 * `remaining` is clamped at zero, so it can never report an overrun: a reader comparing it with
 * zero is asking a question the number cannot answer. The projected spend against the effective
 * limit is the comparison that moves, and both figures come from the balance the entitlement
 * gate itself enforces.
 */
export function beyondTheLimit(balance: Balance): boolean {
  return balance.netSpent.amountCents > balance.effectiveLimit.amountCents;
}
